import styled from '@emotion/styled';
import {Button} from '@mui/base';

import {mixinFocus, mixinSVGIcon} from '@ELib/styles/mixins';

export const StyledSmallButton = styled(Button, {
  shouldForwardProp: (props) => props !== 'fullWidth',
})<{
  fullWidth?: boolean;
}>`
  width: ${({fullWidth}): string => (fullWidth ? '100%' : 'fit-content')};
  height: fit-content;
  border-radius: var(--button-border-radius);
  border-width: var(--button-border-width);
  border-style: solid;
  background-color: var(--button-small-bg-color);
  font-size: var(--font-size-tiny);
  text-transform: uppercase;
  padding: var(--button-small-spacing-inner);

  display: flex;
  gap: var(--button-icon-gutter);
  align-items: center;

  svg {
    ${mixinSVGIcon}
  }

  &:disabled {
    color: var(--button-inactive-bg-color);
    border-color: currentColor;
    cursor: not-allowed;
  }

  &:enabled {
    color: var(--button-small-content-color);
    border-color: currentColor;
    cursor: pointer;

    &:hover {
      color: var(--button-hover-content-color);
      background-color: var(--button-small-hover-bg-color);
      border-color: var(--button-small-hover-bg-color);
    }

    &:active {
      color: var(--button-active-content-color);
      background-color: var(--button-small-active-bg-color);
      border-color: var(--button-small-active-bg-color);
    }
  }

  &.danger {
    color: var(--button-small-danger-content-color);
    background-color: var(--button-small-bg-color);
    border-color: transparent;

    &:hover {
      color: var(--button-hover-content-color);
      background-color: var(--button-danger-bg-color);
      border-color: var(--button-danger-bg-color);
    }

    &:active {
      color: var(--button-active-content-color);
      background-color: var(--button-danger-hover-bg-color);
      border-color: var(--button-danger-hover-bg-color);
    }
  }

  &:focus-visible {
    ${mixinFocus}
  }
`;

export const StyledButton = styled(Button, {
  shouldForwardProp: (props) => props !== 'fullWidth',
})<{
  disabled?: boolean;
  fullWidth?: boolean;
}>`
  width: ${({fullWidth}): string => (fullWidth ? '100%' : 'fit-content')};
  text-transform: uppercase;
  cursor: ${({disabled}): string => (disabled ? 'not-allowed' : 'pointer')};
  border-radius: var(--button-border-radius);
  border-width: var(--button-border-width);
  border-style: solid;
  box-sizing: content-box;
  padding: var(--button-spacing-inner);
  font-size: var(--button-font-size);
  font-weight: var(--button-font-weight);
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: var(--button-icon-gutter);

  :focus {
    ${mixinFocus}
  }

  svg {
    ${mixinSVGIcon}
  }

  color: var(--button-content-color);
  background-color: var(--button-active-bg-color);
  border-color: var(--button-active-bg-color);

  &:hover {
    background-color: var(--button-hover-bg-color);
    border-color: var(--button-hover-bg-color);
  }

  &:disabled {
    background-color: var(--button-inactive-bg-color);
    border-color: var(--button-inactive-bg-color);
  }

  &.new-style {
    border-radius: var(--button-new-style-border-radius);
    padding: var(--button-new-style-spacing-inner);
  }

  &.flat {
    color: var(--button-active-bg-color);
    background-color: var(--button-content-color);
    border-color: var(--button-content-color);

    &:hover {
      color: var(--button-hover-bg-color);
    }

    &:disabled {
      color: var(--button-inactive-bg-color);
    }
  }

  &.outline {
    color: var(--button-active-bg-color);
    background-color: var(--button-content-color);
    border-color: var(--button-active-bg-color);

    &:hover {
      color: var(--button-hover-bg-color);
    }

    &:disabled {
      color: var(--button-inactive-bg-color);
      border-color: var(--button-inactive-bg-color);
    }
  }

  &.danger {
    color: var(--button-content-color);
    background-color: var(--button-danger-bg-color);
    border-color: var(--button-danger-bg-color);

    &:hover {
      background-color: var(--button-danger-hover-bg-color);
      border-color: var(--button-danger-hover-bg-color);
    }

    &:disabled {
      background-color: var(--button-danger-inactive-bg-color);
      border-color: var(--button-danger-inactive-bg-color);
    }
  }
`;

export const StyledSnackbarButton = styled(Button, {
  shouldForwardProp: (props) => props !== 'fullWidth',
})<{
  fullWidth?: boolean;
}>`
  width: ${({fullWidth}): string => (fullWidth ? '100%' : 'fit-content')};
  height: fit-content;
  border-radius: var(--button-border-radius);
  border-width: var(--button-border-width);
  border-style: solid;
  background-color: transparent;
  font-size: var(--font-size-tiny);
  text-transform: uppercase;
  padding: var(--button-small-spacing-inner);
  margin-left: var(--gutter);

  display: flex;
  gap: var(--button-icon-gutter);
  align-items: center;

  color: var(--button-content-color);
  border-color: var(--button-content-color);

  svg {
    ${mixinSVGIcon}
  }

  &:enabled {
    cursor: pointer;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover {
    opacity: 0.8;
  }

  &:focus-visible {
    ${mixinFocus}
  }
`;
